import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Mail, Send, Trash2, RefreshCw, Clock, CheckCircle, XCircle, Crown, Copy, Check, Star } from 'lucide-react';
import api from '../lib/api';

export default function AdminInvitationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copied, setCopied] = useState(null);
  const [newInvite, setNewInvite] = useState({
    email: '',
    name: '',
    subscription: 'free',
    premium_months: 1,
    show_inviter_name: false
  });

  useEffect(() => {
    if (!user?.admin_role) {
      navigate('/dashboard');
      return;
    }
    fetchInvitations();
  }, [user, navigate]);

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/admin/invitations');
      setInvitations(response.data.invitations);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!newInvite.email) return;
    try {
      await api.post('/admin/invitations', newInvite);
      setShowCreateModal(false);
      setNewInvite({ email: '', name: '', subscription: 'free', premium_months: 1, show_inviter_name: false });
      fetchInvitations();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!confirm('Annuler cette invitation ?')) return;
    try {
      await api.delete(`/admin/invitations/${invitationId}`);
      fetchInvitations();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await api.post(`/admin/invitations/${invitationId}/resend`);
      fetchInvitations();
      alert('Invitation renvoyée (validité prolongée de 7 jours)');
    } catch (error) {
      console.error('Error resending invitation:', error);
    }
  };

  const copyInviteLink = (invitation) => {
    // Use invitation_id if available, otherwise use email as identifier
    const identifier = invitation.invitation_id || invitation.email;
    const link = `${window.location.origin}/invitation/${identifier}`;
    navigator.clipboard.writeText(link);
    setCopied(identifier);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-gray-500" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'accepted': return 'Acceptée';
      case 'expired': return 'Expirée';
      case 'cancelled': return 'Annulée';
      default: return 'En attente';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-100 text-gray-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Invitations</h1>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Inviter
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          </div>
        ) : invitations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune invitation envoyée</p>
              <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une invitation
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {invitations.map((inv) => (
              <Card key={inv.invitation_id || inv.email}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{inv.email}</span>
                          {inv.subscription === 'premium' && (
                            <Crown className="w-4 h-4 text-yellow-500" />
                          )}
                          {inv.subscription === 'standard' && (
                            <Star className="w-4 h-4 text-blue-500" />
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(inv.status)}`}>
                            {getStatusIcon(inv.status)}
                            <span className="ml-1">{getStatusLabel(inv.status)}</span>
                          </span>
                        </div>
                        {inv.name && (
                          <p className="text-sm text-muted-foreground">{inv.name}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Invité par: {inv.invited_by_name}</span>
                          <span>Le: {new Date(inv.created_at).toLocaleDateString('fr-FR')}</span>
                          {inv.status === 'pending' && (
                            <span>Expire: {new Date(inv.expires_at).toLocaleDateString('fr-FR')}</span>
                          )}
                        </div>
                        {(inv.subscription === 'premium' || inv.subscription === 'standard') && inv.premium_months > 0 && (
                          <p className="text-xs text-primary mt-1">
                            🎁 {inv.premium_months} mois {inv.subscription === 'premium' ? 'Premium' : 'Standard'} offerts
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {inv.status === 'pending' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => copyInviteLink(inv)}
                            title="Copier le lien"
                          >
                            {copied === (inv.invitation_id || inv.email) ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleResendInvitation(inv.invitation_id || inv.email)}
                            title="Renvoyer"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500"
                            onClick={() => handleCancelInvitation(inv.invitation_id || inv.email)}
                            title="Annuler"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Invitation Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inviter un utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                value={newInvite.email}
                onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                placeholder="utilisateur@example.com"
                className="mt-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nom (optionnel)</label>
              <Input
                value={newInvite.name}
                onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                placeholder="Jean Dupont"
                className="mt-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Abonnement initial</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[
                  { value: 'free', label: 'Gratuit' },
                  { value: 'standard', label: 'Standard' },
                  { value: 'premium', label: 'Premium' }
                ].map((sub) => (
                  <Button
                    key={sub.value}
                    variant={newInvite.subscription === sub.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewInvite({ ...newInvite, subscription: sub.value })}
                  >
                    {sub.label}
                  </Button>
                ))}
              </div>
            </div>
            {(newInvite.subscription === 'premium' || newInvite.subscription === 'standard') && (
              <div>
                <label className="text-sm font-medium">Mois {newInvite.subscription === 'premium' ? 'Premium' : 'Standard'} offerts</label>
                <Input
                  type="number"
                  min="1"
                  value={newInvite.premium_months}
                  onChange={(e) => setNewInvite({ ...newInvite, premium_months: parseInt(e.target.value) || 1 })}
                  className="mt-2"
                />
              </div>
            )}
            <div className="flex items-center space-x-3 pt-2">
              <input
                type="checkbox"
                id="show_inviter_name"
                checked={newInvite.show_inviter_name}
                onChange={(e) => setNewInvite({ ...newInvite, show_inviter_name: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="show_inviter_name" className="text-sm">
                Afficher mon nom dans l'email
                <span className="block text-xs text-muted-foreground">
                  Si décoché, l'invitation sera envoyée au nom de "L'équipe SpenSpot"
                </span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleCreateInvitation} disabled={!newInvite.email}>
              <Send className="w-4 h-4 mr-2" />
              Envoyer l'invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
